import moment from 'moment';

import { validateSchema } from '../../../../../../helpers';
import { goalSchema } from '../../common/utils/validationSchema';
import {
  GoalTypes,
  TargetStages,
  STAGE_ORDER,
  TargetStageStatus,
  TreatmentPlanResourceVersion,
} from '../../common/constants/general';

import type {
  IStageChangeLog,
  ISelectOption,
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
} from '../../../../../../utils/types';
import type { StageHistoryEntry, StageTransitionResult } from './types/general';

export const calculateStageCount = (
  fromStage: TargetStages,
  toStage: TargetStages,
  stageOrder: TargetStages[],
): number => {
  const fromIndex = stageOrder.indexOf(fromStage);
  const toIndex = stageOrder.indexOf(toStage);
  return Math.abs(toIndex - fromIndex);
};

export const transformStageHistory = (
  data: IStageChangeLog[],
  goalType: GoalTypes,
): StageHistoryEntry[] => {
  const sortedData = [...data].sort((a, b) => a.order - b.order);
  const stageOrder = STAGE_ORDER[goalType];

  return sortedData.map((entry) => {
    if (!entry.old_stage) {
      return {
        id: entry.id,
        date: entry.date_stage_changed,
        stage: entry.new_stage,
        reasonForChange: entry.reason_for_change,
        status: TargetStageStatus.Initial,
        dateEditsBlocked: entry.date_edits_blocked || null,
      };
    }

    if (entry.new_stage === TargetStages.Discontinued) {
      const previousStage = data[data.indexOf(entry) - 1]?.old_stage;
      const stage =
        entry.old_stage === TargetStages.OnHold && data.length >= 2
          ? previousStage ?? entry.old_stage
          : entry.old_stage;
      return {
        id: entry.id,
        date: entry.date_stage_changed,
        stage: stage,
        reasonForChange: entry.reason_for_change,
        status: TargetStageStatus.Discontinued,
        dateEditsBlocked: entry.date_edits_blocked,
      };
    }

    if (entry.new_stage === TargetStages.OnHold) {
      return {
        id: entry.id,
        date: entry.date_stage_changed,
        stage: entry.old_stage,
        reasonForChange: entry.reason_for_change,
        status:
          entry.new_stage === TargetStages.OnHold
            ? TargetStageStatus.Hold
            : TargetStageStatus.Discontinued,
        dateEditsBlocked: entry.date_edits_blocked,
      };
    }

    if (entry.old_stage === TargetStages.OnHold || entry.old_stage === TargetStages.Discontinued) {
      return {
        id: entry.id,
        date: entry.date_stage_changed,
        stage: entry.new_stage,
        reasonForChange: entry.reason_for_change,
        status: TargetStageStatus.Resume,
        dateEditsBlocked: entry.date_edits_blocked,
      };
    }

    const oldIndex = stageOrder.indexOf(entry.old_stage);
    const newIndex = stageOrder.indexOf(entry.new_stage);
    const isProgress = newIndex > oldIndex;

    return {
      id: entry.id,
      date: entry.date_stage_changed,
      stage: entry.new_stage,
      reasonForChange: entry.reason_for_change,
      status: isProgress ? TargetStageStatus.Progress : TargetStageStatus.Regress,
      transition: {
        from: entry.old_stage,
        to: entry.new_stage,
        direction: isProgress ? 'forward' : 'backward',
        stageCount: calculateStageCount(entry.old_stage, entry.new_stage, stageOrder),
      },
      dateEditsBlocked: entry.date_edits_blocked,
    };
  });
};

export const getTargetStagesForGoalType = (
  goalType: GoalTypes | null | undefined,
): ISelectOption[] => {
  if (!goalType) {
    return [];
  }
  switch (goalType) {
    case GoalTypes.SkillAcquisition:
      return Object.values(TargetStages)
        .filter((stage) => stage !== TargetStages.InProgress)
        .map((stage) => ({ label: stage, value: stage }));
    case GoalTypes.BehaviorReduction:
    case GoalTypes.Caregiver:
      return Object.values(TargetStages)
        .filter(
          (stage) =>
            stage !== TargetStages.Acquisition &&
            stage !== TargetStages.Generalization &&
            stage !== TargetStages.Maintenance,
        )
        .map((stage) => ({ label: stage, value: stage }));
    default:
      return [];
  }
};

export function getAvailableStages(stageLogs: IStageChangeLog[], goalType: GoalTypes) {
  const baseStages = getTargetStagesForGoalType(goalType);
  const orderArray = STAGE_ORDER[goalType];

  if (stageLogs.length === 0) {
    return baseStages.filter(
      (stage) =>
        ![
          TargetStages.OnHold,
          TargetStages.Resume,
          TargetStages.Discontinued,
          TargetStages.Mastered,
        ].some((disallowedStage) => disallowedStage === stage.value),
    );
  }

  const lastStageLog = stageLogs[stageLogs.length - 1];
  const currentStage = lastStageLog.new_stage;
  if (!currentStage) return [];

  if (currentStage === TargetStages.Discontinued) {
    if (lastStageLog.old_stage === TargetStages.OnHold && stageLogs.length >= 2) {
      const previousLog = stageLogs[stageLogs.length - 2];
      return baseStages.filter((stage) => stage.value === previousLog.old_stage);
    }
    return baseStages.filter((stage) => stage.value === lastStageLog.old_stage);
  }

  if (currentStage === TargetStages.OnHold) {
    if (lastStageLog.old_stage) {
      return baseStages.filter(
        (stage) =>
          stage.value === lastStageLog.old_stage ||
          stage.value === TargetStages.Discontinued,
      );
    }
    return baseStages.filter(
      (stage) =>
        ![TargetStages.Mastered, TargetStages.OnHold, TargetStages.Resume].some(
          (disallowedStage) => disallowedStage === stage.value,
        ),
    );
  }

  if (currentStage === TargetStages.Mastered) {
    return baseStages.filter((stage) =>
      [TargetStages.Generalization, TargetStages.Maintenance, TargetStages.InProgress].some(
        (availableStage) => availableStage === stage.value,
      ),
    );
  }

  if (
    [TargetStages.Baseline, TargetStages.Acquisition, TargetStages.InProgress].includes(
      currentStage,
    )
  ) {
    const currentIndex = orderArray.indexOf(currentStage);
    const nextStage = currentIndex < orderArray.length - 1 ? orderArray[currentIndex + 1] : null;
    return baseStages.filter(
      (stage) =>
        stage.value === nextStage ||
        stage.value === TargetStages.OnHold ||
        stage.value === TargetStages.Discontinued,
    );
  }

  const currentIndex = orderArray.indexOf(currentStage);
  const prevStage = currentIndex > 0 ? orderArray[currentIndex - 1] : null;
  const nextStage = currentIndex < orderArray.length - 1 ? orderArray[currentIndex + 1] : null;
  return baseStages.filter(
    (stage) =>
      stage.value === prevStage ||
      stage.value === nextStage ||
      stage.value === TargetStages.OnHold ||
      stage.value === TargetStages.Discontinued,
  );
}

export const isValidStageTransition = (
  stageLogs: IStageChangeLog[],
  selectedStage: TargetStages | null,
  goalType: GoalTypes,
  selectedDate: string,
): StageTransitionResult => {
  if (selectedStage === null) {
    return {
      isValid: false,
      reason: 'Invalid stage selection',
    };
  }

  if (stageLogs.length === 0) {
    return {
      isValid: ![
        TargetStages.OnHold,
        TargetStages.Resume,
        TargetStages.Discontinued,
        TargetStages.Mastered,
      ].includes(selectedStage),
      reason: 'The initial stage cannot be a terminating stage',
    };
  }

  const lastStageLog = stageLogs[stageLogs.length - 1];

  if (selectedDate) {
    if (lastStageLog.date_stage_changed === moment(selectedDate).format('YYYY-MM-DD')) {
      return {
        isValid: false,
        reason: 'A stage change on the same date is not allowed.',
      };
    }
    if (moment(selectedDate).isBefore(moment(lastStageLog.date_stage_changed))) {
      return {
        isValid: false,
        reason: 'The selected date cannot be earlier than the date of the last stage change',
      };
    }
    if (!lastStageLog.date_stage_changed) {
      return {
        isValid: false,
        reason: 'The latest stage change does not have a date',
      };
    }
  }

  const currentStage = lastStageLog.new_stage;
  if (!currentStage) return { isValid: false, reason: 'Current stage not found' };

  if (selectedStage === TargetStages.Discontinued) {
    return {
      isValid: ![TargetStages.Resume, TargetStages.Discontinued, TargetStages.Mastered].includes(
        currentStage,
      ),
      reason: 'Cannot move to Discontinued from Resume, Discontinued, or Mastered.',
    };
  }

  if (currentStage === TargetStages.Discontinued) {
    if (lastStageLog.old_stage === TargetStages.OnHold && stageLogs.length >= 2) {
      const previousLog = stageLogs[stageLogs.length - 2];
      return {
        isValid: selectedStage === previousLog.old_stage,
        reason: 'Can only return to the stage before OnHold',
      };
    }
    return {
      isValid: selectedStage === lastStageLog.old_stage,
      reason: 'Can only return to the immediate previous stage after Discontinued.',
    };
  }

  if (selectedStage === TargetStages.OnHold) {
    return {
      isValid: ![TargetStages.Mastered, TargetStages.OnHold, TargetStages.Resume].includes(
        currentStage,
      ),
      reason: 'Cannot put Mastered, OnHold, or Resume stages on hold.',
    };
  }

  if (currentStage === TargetStages.OnHold) {
    if (lastStageLog.old_stage) {
      return {
        isValid: selectedStage === lastStageLog.old_stage,
        reason: 'Can only return to the previous stage or move to Discontinued from OnHold.',
      };
    }
    return {
      isValid: ![TargetStages.Mastered, TargetStages.OnHold, TargetStages.Resume].includes(
        selectedStage,
      ),
      reason: 'Can only return to a valid stage or move to Discontinued from OnHold.',
    };
  }

  if (currentStage === TargetStages.Mastered) {
    return {
      isValid: [
        TargetStages.Generalization,
        TargetStages.Maintenance,
        TargetStages.InProgress,
      ].includes(selectedStage),
      reason: 'Can only move to Generalization, Maintenance, or In Progress from Mastered',
    };
  }

  const stageOrder = STAGE_ORDER[goalType];
  const currentIndex = stageOrder.indexOf(currentStage);
  const selectedIndex = stageOrder.indexOf(selectedStage);

  if (currentIndex === -1 || selectedIndex === -1) {
    return {
      isValid: false,
      reason: 'Invalid stage selection',
    };
  }

  if (
    [TargetStages.Baseline, TargetStages.Acquisition].includes(currentStage) &&
    selectedIndex < currentIndex
  ) {
    return {
      isValid: false,
      reason: `Cannot move backward from the ${currentStage} stage.`,
    };
  }

  return {
    isValid: Math.abs(selectedIndex - currentIndex) === 1,
    reason: 'The selected stage must be adjacent to the current stage.',
  };
};

export const getV2TargetDuplicationData = (target: ITreatmentPlanGoalTarget) => {
  return {
    description: target.description ?? '',
    instruction: target.instruction ?? '',
    stage: TargetStages.Baseline,
    expanded: true,
    is_event_duration_required: false,
    is_active: true,
    is_editing: true,
    dirty: true,
  };
};

export const hasV2TargetBeenDeactivated = (target: ITreatmentPlanGoalTarget): boolean => {
  const { stage_change_logs } = target;
  if (!stage_change_logs?.length) return false;
  const sortedLogs = [...stage_change_logs].sort((a, b) => b.order - a.order);
  const latestLog = sortedLogs[0];
  const hasTargetBeenDisabled =
    latestLog.new_stage === TargetStages.OnHold ||
    latestLog.new_stage === TargetStages.Discontinued ||
    latestLog.new_stage === TargetStages.Baseline;
  return hasTargetBeenDisabled;
};

export const isV2TargetActive = (target: ITreatmentPlanGoalTarget): boolean => {
  const { stage_change_logs } = target;
  if (!stage_change_logs?.length) return false;
  const sortedLogs = [...stage_change_logs].sort((a, b) => b.order - a.order);
  const latestLog = sortedLogs[0];
  if (
    latestLog.new_stage === TargetStages.OnHold ||
    latestLog.new_stage === TargetStages.Mastered ||
    latestLog.new_stage === TargetStages.Discontinued ||
    latestLog.new_stage === TargetStages.Baseline
  ) {
    return false;
  }
  return true;
};

export const shouldUnlinkSAGGoal = (goal: ITreatmentPlanGoal) => {
  return goal.targets?.every((target) => hasV2TargetBeenDeactivated(target));
};

export const willV2GoalBeActive = (goal: ITreatmentPlanGoal) => {
  return goal.targets?.some((target) => isV2TargetActive(target));
};

export const isTargetPracticable = (target: ITreatmentPlanGoalTarget): boolean => {
  if (target.stage_change_logs?.length) {
    const sortedLogs = [...target.stage_change_logs].sort((a, b) => b.order - a.order);
    for (const log of sortedLogs) {
      if (log.date_edits_blocked && log.date_stage_changed) {
        return ![
          TargetStages.OnHold,
          TargetStages.Mastered,
          TargetStages.Discontinued,
          TargetStages.Baseline,
        ].includes(log.new_stage);
      }
    }
  }
  if (target.last_activation_log) {
    return target.last_activation_log.is_active;
  }
  return false;
};

export const getTargetTooltipMessage = (target: ITreatmentPlanGoalTarget): string => {
  if (target.stage_change_logs?.length) {
    const sortedLogs = [...target.stage_change_logs].sort((a, b) => b.order - a.order);
    for (const log of sortedLogs) {
      if (log.date_edits_blocked && log.date_stage_changed) {
        const formattedDate = moment(log.date_stage_changed).format('MM/DD/YY');
        return `The target stage in the last signed TP is <strong>${log.new_stage}</strong> since <strong>${formattedDate}</strong>; therefore, the BT will ${
          [
            TargetStages.OnHold,
            TargetStages.Mastered,
            TargetStages.Discontinued,
            TargetStages.Baseline,
          ].includes(log.new_stage)
            ? 'not'
            : ''
        } practice it during treatment.`;
      }
    }
  }
  if (target.last_activation_log) {
    const formattedDate = moment(target.last_activation_log.date).format('MM/DD/YY');
    return target.last_activation_log.is_active
      ? `The target was activated on <strong>${formattedDate}</strong> in the last signed TP and available for the BT to practice during treatment.`
      : `The target was deactivated on <strong>${formattedDate}</strong> in the last signed TP; therefore, the BT will not practice it during treatment.`;
  }
  if (target.stage_change_logs?.length) {
    return "The TP hasn't been signed after the target was created; therefore, the BT will not practice it during treatment.";
  }
  return "The target doesn't have a stage; therefore, the BT will not practice it during treatment.";
};

export const validateV2Goal = (goal: ITreatmentPlanGoal) => {
  const errors: Record<string, boolean> = {};

  const {
    dirty: _dirty,
    is_editing: _isGoalEditing,
    is_linkable: _is_linkable,
    expanded: _expanded,
    ...rest
  } = goal;
  const restWithGoalVersion = {
    ...rest,
    goal_version: TreatmentPlanResourceVersion.V2,
  };
  Object.keys(restWithGoalVersion).forEach((key) => {
    const isValidField = validateSchema(goalSchema, key, restWithGoalVersion);
    errors[key] = !isValidField;
  });
  return errors;
};
